/**
 * Core game engine — manages the fixed-timestep game loop.
 *
 * Uses requestAnimationFrame with a fixed update step (for deterministic physics)
 * and variable render interpolation (for smooth visuals at any refresh rate).
 */

export interface EngineCallbacks {
  update(dt: number): void;
  render(interpolation: number): void;
}

const FIXED_DT = 1 / 120; // 120 Hz physics
const MAX_FRAME_TIME = 0.25; // Cap to avoid spiral of death

export class Engine {
  private callbacks: EngineCallbacks;
  private accumulator = 0;
  private lastTime = 0;
  private rafId = 0;
  private running = false;

  // Performance metrics
  fps = 0;
  private frameCount = 0;
  private fpsTimer = 0;

  /** The fixed timestep in seconds */
  readonly fixedDt = FIXED_DT;

  constructor(callbacks: EngineCallbacks) {
    this.callbacks = callbacks;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now() / 1000;
    this.accumulator = 0;
    this.frameCount = 0;
    this.fpsTimer = 0;
    this.rafId = requestAnimationFrame(this.loop);
  }

  stop(): void {
    this.running = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  }

  private loop = (timestamp: number): void => {
    if (!this.running) return;

    const now = timestamp / 1000;
    let frameTime = now - this.lastTime;
    this.lastTime = now;

    // Clamp to prevent spiral of death after tab switch / debugger pause
    if (frameTime > MAX_FRAME_TIME) {
      frameTime = MAX_FRAME_TIME;
    }

    // FPS tracking
    this.frameCount++;
    this.fpsTimer += frameTime;
    if (this.fpsTimer >= 1.0) {
      this.fps = this.frameCount;
      this.frameCount = 0;
      this.fpsTimer -= 1.0;
    }

    // Fixed-timestep updates
    this.accumulator += frameTime;
    while (this.accumulator >= FIXED_DT) {
      this.callbacks.update(FIXED_DT);
      this.accumulator -= FIXED_DT;
    }

    // Render with interpolation factor
    const interpolation = this.accumulator / FIXED_DT;
    this.callbacks.render(interpolation);

    this.rafId = requestAnimationFrame(this.loop);
  };
}
